import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useUnseenUnlocks, useMarkSeen, useMarkViewed, coinArt, type CaseCoin } from '@/hooks/useAchievements'
import { UnlockModal } from './UnlockModal'

const FEED = '/app'
const TOASTED_KEY = 'gg_coin_toasted'

function toastedIds(): Set<string> {
  try { return new Set(JSON.parse(sessionStorage.getItem(TOASTED_KEY) ?? '[]') as string[]) } catch { return new Set() }
}
function rememberToasted(ids: Set<string>) {
  try { sessionStorage.setItem(TOASTED_KEY, JSON.stringify([...ids])) } catch { /* private mode */ }
}

/**
 * Celebrates newly earned coins (Brian's coin-unlock.js v2, coins.html v1.3;
 * badge spec §2).
 *
 * On Your feed: the full moment, one coin at a time ("1 of 2"), never stacked.
 * Anywhere else: a toast for a moment (the coin, its name, any seeds), and the
 * coin stays queued for the feed's full moment. Never on Record. The queue is
 * the database's: earned and not yet celebrated (seen_at), so a coin earned
 * while the app was closed plays on the next open of the feed.
 */
export function UnlockWatcher() {
  const unseen = useUnseenUnlocks()
  const markSeen = useMarkSeen()
  const markViewed = useMarkViewed()
  const location = useLocation()
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [batch, setBatch] = useState(0)
  const onFeed = location.pathname === FEED || location.pathname === `${FEED}/`
  const onRecord = location.pathname.startsWith('/app/record')

  const queue = unseen.filter((c) => !dismissed.has(c.id))

  // "1 of N": the size of the run when it started.
  useEffect(() => {
    if (!onFeed) return
    if (queue.length === 0) setBatch(0)
    else setBatch((b) => Math.max(b, queue.length + [...dismissed].filter((id) => unseen.some((c) => c.id === id)).length))
  }, [queue.length, onFeed]) // eslint-disable-line react-hooks/exhaustive-deps

  // Forget dismissals the database has caught up with.
  useEffect(() => {
    setDismissed((d) => {
      const live = new Set([...d].filter((id) => unseen.some((c) => c.id === id)))
      return live.size === d.size ? d : live
    })
  }, [unseen])

  if (onRecord) return null
  if (!onFeed) return <UnlockToasts coins={queue} />

  const current = queue[0]
  if (!current) return null
  const index = Math.max(1, batch - queue.length + 1)

  return (
    <UnlockModal
      key={current.id}
      coin={current}
      index={index}
      total={Math.max(batch, queue.length)}
      onClose={() => {
        // Hide at once, then persist: the unviewed count goes up now.
        setDismissed((d) => new Set(d).add(current.id))
        markSeen.mutate(current.id)
      }}
      onSee={() => {
        setDismissed(new Set(unseen.map((c) => c.id)))
        markViewed.mutate()
        navigate('/app/achievements')
      }}
    />
  )
}

/** A coin earned away from the feed: a toast for a moment, once a session. */
function UnlockToasts({ coins }: { coins: CaseCoin[] }) {
  const [now, setNow] = useState<CaseCoin | null>(null)
  const [on, setOn] = useState(false)
  const [tick, setTick] = useState(0)
  const seen = useRef<Set<string>>(toastedIds())
  const busy = useRef(false)
  const timers = useRef<number[]>([])
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), [])
  const ids = coins.map((c) => c.id).join(',')

  useEffect(() => {
    if (busy.current) return
    const next = coins.find((c) => !seen.current.has(c.id))
    if (!next) return
    busy.current = true
    seen.current.add(next.id)
    rememberToasted(seen.current)
    timers.current.push(
      window.setTimeout(() => { setNow(next); setOn(true) }, 900),
      window.setTimeout(() => setOn(false), 900 + 2300),
      window.setTimeout(() => { setNow(null); busy.current = false; setTick((x) => x + 1) }, 900 + 2300 + 260),
    )
  }, [ids, tick]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!now) return null
  return (
    <div className={`coin-toast${on ? ' is-on' : ''}`} role="status">
      <img src={coinArt(now.id, true, 256)} alt="" width={34} height={34} />
      <span>
        <span className="coin-toast__k">Coin unlocked</span>
        <span className="coin-toast__n">{now.name}{now.reward_seeds > 0 && <span className="coin-toast__s">+{now.reward_seeds}</span>}</span>
      </span>
    </div>
  )
}
